/**
 * Mapping des types de contenu pour normalisation et affichage.
 *
 * Comme les plateformes, ce n'est pas une taxonomie métier figée : une valeur
 * non reconnue reste visible/filtrable (nettoyée) plutôt que rattachée à un
 * bucket générique.
 */

export type NormalizedContentType = string;

export interface ContentTypeMapping {
  normalized: string;
  displayName: string;
  variants: string[];
}

export const CONTENT_TYPES_MAPPING: ContentTypeMapping[] = [
  { normalized: 'Facecam', displayName: '🎥 Facecam', variants: ['facecam', 'face cam'] },
  {
    normalized: 'Tutoriel',
    displayName: '📖 Tutoriel',
    variants: [
      'tutoriel', 'tutorial', 'tuto', 'how to', 'howto', 'demonstration',
      'tuto / demonstration', 'tuto demonstration', 'tutorial / demonstration', 'tutorial demonstration',
    ],
  },
  { normalized: 'Unboxing', displayName: '📦 Unboxing', variants: ['unboxing', 'unboxings', 'unbox'] },
  {
    normalized: 'Humour',
    displayName: '😄 Humour',
    variants: ['humour', 'humor', 'humoristique', 'humorous', 'funny', 'comedy'],
  },
  { normalized: 'Voice-over', displayName: '🎙️ Voice-over', variants: ['voice-over', 'voice over', 'voiceover'] },
  {
    normalized: 'Photography',
    displayName: '📸 Photography',
    variants: ['photography', 'photo', 'photographie', 'photographies'],
  },
  { normalized: 'ASMR', displayName: '🌸 ASMR', variants: ['asmr'] },
  { normalized: 'Aesthetic', displayName: '✨ Aesthetic', variants: ['aesthetic', 'esthetic', 'esthetique', 'aesthetique'] },
  {
    normalized: 'Micro-trottoir',
    displayName: '🎤 Micro-trottoir',
    variants: ['micro-trottoir', 'micro trottoir', 'street interview', 'street interviews', 'vox pop'],
  },
];

export const CANONICAL_CONTENT_TYPES: Array<{ id: string; label: string }> = CONTENT_TYPES_MAPPING.map(
  ({ normalized, displayName }) => ({ id: normalized, label: displayName })
);

function normalizeSearchTerm(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/['']/g, '')
    .replace(/[-_/]/g, ' ')
    .replace(/\s+/g, ' ');
}

const variantsToNormalizedMap = new Map<string, string>();
CONTENT_TYPES_MAPPING.forEach(({ normalized, variants }) => {
  variantsToNormalizedMap.set(normalizeSearchTerm(normalized), normalized);
  variants.forEach(variant => variantsToNormalizedMap.set(normalizeSearchTerm(variant), normalized));
});

/**
 * Normalise un type de contenu : casse/accents/espaces/tirets tolérés. Une valeur
 * non reconnue est retournée nettoyée plutôt que rattachée à un bucket générique.
 */
export function normalizeContentType(input: string): NormalizedContentType {
  const trimmed = typeof input === 'string' ? input.trim() : '';
  if (!trimmed) return 'Autre';

  const match = variantsToNormalizedMap.get(normalizeSearchTerm(trimmed));
  if (match) return match;

  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export function getContentTypeDisplayName(normalized: NormalizedContentType): string {
  const match = CONTENT_TYPES_MAPPING.find(c => c.normalized === normalized);
  return match ? match.displayName : normalized;
}

/**
 * Traite une liste de types de contenu bruts et retourne les valeurs normalisées uniques.
 */
export function processInputContentTypes(inputs: string[]): NormalizedContentType[] {
  const set = new Set<string>();
  inputs.forEach(input => {
    if (!input || typeof input !== 'string') return;
    set.add(normalizeContentType(input));
  });
  return Array.from(set).sort();
}
